import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import type { ILandingPage } from 'src/pages/layouts/entities/types';
import type { TLocaleDirection } from 'src/providers/l10n/types';
import { LocaleService } from 'src/services/locale/locale.service';
import { LocaleCookieService } from 'src/services/locale-cookie/locale-cookie.service';
import { TypicalProjectsRestService } from 'src/services/typical-projects-rest/typical-projects-rest.service';
import { UrlService } from 'src/services/url/url.service';
import type { ILocaleWithLink } from 'src/services/url-localise/entities';
import { LocalisedLinkLevel } from 'src/services/url-localise/entities';
import { UrlLocaliseService } from 'src/services/url-localise/url-localise.service';
import { clearUrl } from 'src/shared/helpers/clear-url/clear-url';

@Injectable()
export class PageLocalizationService {
    private readonly document = inject<Document>(DOCUMENT);
    private readonly restService = inject(TypicalProjectsRestService);
    private readonly router = inject(Router);
    private readonly urlLocaliseService = inject(UrlLocaliseService);
    private readonly localeService = inject(LocaleService);
    private readonly localeCookieService = inject(LocaleCookieService);
    private readonly urlService = inject(UrlService);

    public setPageLocalization(data?: ILandingPage): void {
        // for local development without Page List in the environment, also you have to disable CORS in browser
        const canonicalUrl = data?.seo_meta?.canonical || data?.seoMeta?.canonical;
        const origin = this.getOriginUrl();
        const currentLocaleSlug = this.localeService.getCurrentLocaleSlug();
        const currentLocaleDir = this.localeService.getCurrentLocaleDirection();

        this.localeCookieService.setLocaleCookie();
        this.setHtmlLangAndDirAttribute(currentLocaleSlug, currentLocaleDir);

        if (!this.isCanonicalPresent()) {
            this.setCanonicalUrl(canonicalUrl, origin);
        }

        if (!this.isAlternatePresent()) {
            this.setAlternateTags(this.ensureLocalisedLinks(data));
        }
    }

    public generateLocalisedLinks(data?: ILandingPage): ILocaleWithLink[] {
        const origin = this.getOriginUrl();
        const clearedRoute = clearUrl(this.router.url);
        const currentLocaleSlug = this.localeService.getCurrentLocaleSlug();
        const pathToCurrentLocaleSlug = this.localeService.getPathToLocaleSlug(currentLocaleSlug);
        const pathToFirstUrlSegment = this.urlService.getPathToFirstUrlSegment();

        return this.urlLocaliseService.generateLocalisedLinks(
            origin + pathToCurrentLocaleSlug + clearedRoute,
            origin + pathToFirstUrlSegment,
            origin,
            data?.localizedUrlsList
        );
    }

    private ensureLocalisedLinks(data?: ILandingPage): ILocaleWithLink[] {
        const existingLinks = this.urlLocaliseService.getCurrentLocalisedLinks();

        if (existingLinks.length > 0) {
            return existingLinks;
        }

        return this.generateLocalisedLinks(data);
    }

    private setHtmlLangAndDirAttribute(localeSlug: string, localeDirection: TLocaleDirection): void {
        const htmlTag = this.document.querySelector('html');
        htmlTag.setAttribute('lang', localeSlug);
        htmlTag.setAttribute('dir', localeDirection);
    }

    private setAlternateTags(pageLocaleLinks: ILocaleWithLink[]): void {
        const defaultLocaleSlug = this.localeService.getDefaultLocaleSlug();

        pageLocaleLinks.forEach((pageLocaleLink) => {
            if (pageLocaleLink.level === LocalisedLinkLevel.CurrentPage) {
                const alternateLocaleCode = pageLocaleLink.alternateAlias || pageLocaleLink.slug;

                if (defaultLocaleSlug === pageLocaleLink.slug) {
                    this.appendAlternateTag('x-default', pageLocaleLink.url);
                }

                this.appendAlternateTag(alternateLocaleCode, pageLocaleLink.url);
            }
        });
    }

    private appendAlternateTag(localeCode: string, url: string): void {
        const alternateTag = this.document.createElement('link');

        alternateTag.setAttribute('rel', 'alternate');
        alternateTag.setAttribute('hreflang', localeCode);
        alternateTag.setAttribute('href', url);
        this.document.head.appendChild(alternateTag);
    }

    private setCanonicalUrl(url: string, hostname: string): void {
        const link: HTMLLinkElement = this.document.createElement('link');

        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', url ? url : hostname + this.urlService.getFullPath());
        this.document.head.appendChild(link);
    }

    private getOriginUrl() {
        return 'https://' + this.restService.getHostname();
    }

    private isCanonicalPresent(): boolean {
        const linkElement = this.document.head.querySelector('link[rel="canonical"]');
        return linkElement !== null && linkElement !== undefined;
    }

    private isAlternatePresent(): boolean {
        const linkElement = this.document.head.querySelector('link[rel="alternate"]');
        return linkElement !== null && linkElement !== undefined;
    }
}
